import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { clearGroupsCache } from '../utils/useGroups';
import { ROUTE_GROUPS } from '../utils/groups';
import logo from '../the217_logo_transparent-new.svg';

// ── Icons ──────────────────────────────────────────────────────────────────────

function ChevronIcon({ open }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
      className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function CollapseIcon({ collapsed }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
      {collapsed ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
      )}
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

// ── Wordmark ───────────────────────────────────────────────────────────────────
// Expanded: "Project (217)" — white / light-gray parens / orange 217
// Collapsed: "217" — orange, larger for legibility

function Wordmark({ collapsed }) {
  if (collapsed) {
    return (
      <span style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        fontSize: '18px',
        fontWeight: 700,
        color: 'var(--color-orange)',
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}>
        217
      </span>
    );
  }

  return (
    <span style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
      fontSize: '18px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1,
      display: 'flex',
      alignItems: 'baseline',
      gap: '1px',
    }}>
      <span style={{ color: '#FFFFFF', fontWeight: 700 }}>Project</span>
      <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 300 }}>&nbsp;(</span>
      <span style={{ color: 'var(--color-orange)', fontWeight: 700 }}>217</span>
      <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 300 }}>)</span>
    </span>
  );
}

// ── Nav config ─────────────────────────────────────────────────────────────────

export const navItems = [
  {
    to: '/',
    label: 'Home',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>
    ),
  },
  {
    label: 'Events',
    requiredGroups: ROUTE_GROUPS['/events/id-matching'],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    ),
    children: [
      { to: '/events/id-matching', label: 'ID Matching' },
    ],
  },
  {
    to: '/membership',
    label: 'Digital Passes',
    requiredGroups: ROUTE_GROUPS['/membership'],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0Z" />
      </svg>
    ),
  },
  {
    label: 'Fulfillment',
    requiredGroups: ROUTE_GROUPS['/fulfillment'],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
      </svg>
    ),
    children: [
      { to: '/dashboard',   label: 'Dashboard' },
      { to: '/fulfillment', label: 'Tracker' },
      { to: '/promotions',  label: 'Promotions' },
    ],
  },
  {
    to: '/aisrequests',
    label: 'AIS Requests',
    requiredGroups: ROUTE_GROUPS['/aisrequests'],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
  },
];

export const adminNavItems = [
  {
    to: '/admin',
    label: 'Users',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
      </svg>
    ),
  },
];

// ── Route metadata ─────────────────────────────────────────────────────────────

export const ROUTE_LABELS = {
  '/':                   'Home',
  '/events/id-matching': 'ID Matching',
  '/membership':         'Digital Passes',
  '/fulfillment':        'Tracker',
  '/promotions':         'Promotions',
  '/dashboard':          'Dashboard',
  '/aisrequests':        'AIS Requests',
  '/admin':              'Admin Panel',
  '/profile':            'My Profile',
  '/access-denied':      'Access Denied',
};

export const FULL_HEIGHT_ROUTES = [
  '/fulfillment',
  '/promotions',
  '/profile',
  '/dashboard',
  '/events/id-matching',
];

// ── NavItem ────────────────────────────────────────────────────────────────────

function NavItem({ item, collapsed, userGroups, onNavigate }) {
  const location = useLocation();
  const childPaths = item.children?.map(c => c.to) || [];
  const isParentActive = childPaths.some(p => location.pathname === p);
  const [open, setOpen] = useState(isParentActive);

  if (item.requiredGroups && !item.requiredGroups.some(g => userGroups.includes(g))) {
    return null;
  }

  const baseClass = 'group relative flex items-center gap-3 py-2.5 px-3 rounded-lg font-medium text-sm transition-colors duration-150';
  const active    = 'bg-white/10 text-white';
  const inactive  = 'text-white/60 hover:bg-white/[0.07] hover:text-white';

  if (!item.children) {
    const isActive = location.pathname === item.to;
    return (
      <Link
        to={item.to}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        className={`${baseClass} ${isActive ? active : inactive} ${collapsed ? 'justify-center' : ''}`}
      >
        {/* Active indicator — right side dot, matches Promotions Manager pattern */}
        {!collapsed && isActive && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-orange-500" />
        )}
        <span className={isActive ? 'text-orange-500' : 'text-current'}>{item.icon}</span>
        {!collapsed && <span className="truncate flex-1">{item.label}</span>}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={`${baseClass} w-full ${isParentActive ? active : inactive} ${collapsed ? 'justify-center' : ''}`}
      >
        <span className={isParentActive ? 'text-orange-500' : 'text-current'}>{item.icon}</span>
        {!collapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
        {!collapsed && <ChevronIcon open={open} />}
      </button>

      {!collapsed && open && (
        <div className="ml-4 mt-1 pl-3 border-l border-white/10 space-y-0.5">
          {item.children.map(child => {
            const isActive = location.pathname === child.to;
            return (
              <Link
                key={child.to}
                to={child.to}
                onClick={onNavigate}
                className={`flex items-center gap-2 py-2.5 px-3 rounded-md text-sm transition-colors duration-150 ${
                  isActive ? 'text-white bg-white/10 font-medium' : 'text-white/60 hover:text-white hover:bg-white/[0.06]'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-orange-500' : 'bg-white/35'}`} />
                <span className="flex-1">{child.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── SidebarHeader ──────────────────────────────────────────────────────────────
// Wordmark and collapse toggle live in the same row — no absolute positioning,
// no overlap possible in either expanded or collapsed state.

function SidebarHeader({ collapsed, onToggle }) {
  return (
    <div
      className="flex items-center shrink-0 px-3"
      style={{
        height: '56px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {/* Wordmark fills available space */}
      <div className={`flex-1 flex ${collapsed ? 'justify-center' : 'justify-start'} items-center min-w-0`}>
        <Wordmark collapsed={collapsed} />
      </div>

      {/* Toggle button — only visible when expanded; collapsed state uses the
          wordmark area itself as a click target to re-expand */}
      {!collapsed && (
        <button
          onClick={onToggle}
          className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/[0.06] hover:bg-white/[0.11] text-white/50 hover:text-white transition-colors shrink-0 ml-2"
          title="Collapse sidebar"
        >
          <CollapseIcon collapsed={false} />
        </button>
      )}
    </div>
  );
}

// ── UserMenu ───────────────────────────────────────────────────────────────────
// Circle avatar + full name + vertical dots that open a small popover with
// "View Profile" and "Sign Out" actions.

function UserMenu({ givenName, familyName, signOut, onNavigate, collapsed }) {
  const [open, setOpen] = useState(false);
  const menuRef         = React.useRef(null);

  // Derive initials — up to two characters from given + family name
  const initials = [givenName, familyName]
    .map(n => n?.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .join('') || '?';

  // Full display name — fall back gracefully if attributes aren't set yet
  const fullName = [givenName, familyName].filter(Boolean).join(' ') || 'My Account';

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const handleSignOut = () => {
    setOpen(false);
    sessionStorage.clear();
    clearGroupsCache();
    window.history.replaceState(null, '', '/');
    signOut();
  };

  const handleViewProfile = () => {
    setOpen(false);
    if (onNavigate) onNavigate();
  };

  return (
    <div ref={menuRef} className="p-2.5 shrink-0" style={{ position: 'relative' }}>
      <div className="flex items-center gap-2.5 p-2 rounded-xl border border-white/10 bg-white/[0.04]">

        {/* Circle avatar */}
        <div
          className="shrink-0 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold"
          style={{ width: 34, height: 34, fontSize: 13, letterSpacing: '-0.01em' }}
        >
          {initials}
        </div>

        {/* Name — hidden when collapsed */}
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate leading-tight">{fullName}</p>
          </div>
        )}

        {/* Vertical three-dot trigger — hidden when collapsed */}
        {!collapsed && (
          <button
            onClick={() => setOpen(o => !o)}
            aria-label="Account menu"
            className="flex flex-col items-center justify-center gap-[3px] w-7 h-7 rounded-lg hover:bg-white/10 transition-colors shrink-0"
          >
            {[0,1,2].map(i => (
              <span key={i} className="w-[3px] h-[3px] rounded-full bg-white/40" />
            ))}
          </button>
        )}
      </div>

      {/* Popover menu */}
      {open && (
        <div
          className="absolute left-2.5 right-2.5 rounded-xl border border-white/10 overflow-hidden"
          style={{
            bottom: 'calc(100% - 8px)',
            background: 'var(--color-navy-mid)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            zIndex: 50,
          }}
        >
          <Link
            to="/profile"
            onClick={handleViewProfile}
            className="flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium text-white/80 hover:bg-white/[0.08] hover:text-white transition-colors"
          >
            {/* User icon */}
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0 text-white/50">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
            </svg>
            View Profile
          </Link>

          <div className="h-px bg-white/[0.08] mx-3" />

          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium transition-colors"
            style={{ color: 'var(--color-error)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.10)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {/* Logout icon */}
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// ── SidebarContent ─────────────────────────────────────────────────────────────

function SidebarContent({ collapsed, onToggle, userGroups, groups, givenName, familyName, signOut, onNavigate }) {
  const isAdminOrAbove = groups.includes('admin') || groups.includes('superadmin');

  return (
    <>
      {/* Header row — wordmark + collapse toggle, always in normal flow */}
      <SidebarHeader collapsed={collapsed} onToggle={onToggle} />

      {/* When collapsed, a small chevron acts as the expand trigger */}
      {collapsed && (
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center py-2 text-white/30 hover:text-white/60 transition-colors"
          title="Expand sidebar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}

      <nav className="flex-1 px-2.5 py-2 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <NavItem key={item.label} item={item} collapsed={collapsed} userGroups={userGroups} onNavigate={onNavigate} />
        ))}

        {isAdminOrAbove && (
          <div className="mt-4">
            {!collapsed && (
              <p className="px-3 pb-1.5 text-[10px] font-semibold text-white/30 uppercase tracking-widest">
                Admin Tools
              </p>
            )}
            {collapsed && <div className="border-t border-white/10 my-2 mx-2" />}
            <div className="space-y-0.5">
              {adminNavItems.map(item => (
                <NavItem key={item.label} item={item} collapsed={collapsed} userGroups={userGroups} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        )}
      </nav>

      <UserMenu
        givenName={givenName}
        familyName={familyName}
        signOut={signOut}
        onNavigate={onNavigate}
        collapsed={collapsed}
      />
    </>
  );
}

// ── DesktopSidebar ─────────────────────────────────────────────────────────────

export function DesktopSidebar({ collapsed, onToggle, userGroups, groups, givenName, familyName, signOut }) {
  return (
    <aside
      className={`hidden lg:flex relative flex-col overflow-hidden transition-all duration-300 shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
      style={{ background: 'var(--color-navy)' }}
    >
      <SidebarContent
        collapsed={collapsed}
        onToggle={onToggle}
        userGroups={userGroups}
        groups={groups}
        givenName={givenName}
        familyName={familyName}
        signOut={signOut}
        onNavigate={undefined}
      />
    </aside>
  );
}

// ── MobileTopBar ───────────────────────────────────────────────────────────────

export function MobileTopBar({ onMenuOpen, givenName, familyName }) {
  const location = useLocation();
  const label    = ROUTE_LABELS[location.pathname] || 'Portal';
  const initials = [givenName, familyName]
    .map(n => n?.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .join('') || '?';

  return (
    <header
      className="lg:hidden flex items-center justify-between px-4 shrink-0"
      style={{ height: '56px', background: 'var(--color-navy)' }}
    >
      <button onClick={onMenuOpen} className="flex items-center justify-center w-11 h-11 -ml-2 text-white/80 hover:text-white transition-colors" aria-label="Open menu">
        <HamburgerIcon />
      </button>
      <span className="text-white font-semibold text-sm">{label}</span>
      <Link
        to="/profile"
        className="flex items-center justify-center rounded-full bg-orange-500 text-white font-bold text-xs shrink-0"
        style={{ width: 34, height: 34, fontSize: 13, letterSpacing: '-0.01em' }}
      >
        {initials}
      </Link>
    </header>
  );
}

// ── MobileSidebar ──────────────────────────────────────────────────────────────

export function MobileSidebar({ open, onClose, userGroups, groups, givenName, familyName, signOut }) {
  const location = useLocation();
  useEffect(() => { onClose(); }, [location.pathname]); // eslint-disable-line
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {open && (
        <div className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
      )}
      <div
        className="lg:hidden fixed inset-y-0 left-0 flex flex-col w-full max-w-xs z-50"
        style={{
          background: 'var(--color-navy)',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="flex items-center justify-between px-4 shrink-0" style={{ height: '56px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Wordmark collapsed={false} />
          <button onClick={onClose} className="flex items-center justify-center w-11 h-11 text-white/70 hover:text-white transition-colors" aria-label="Close menu">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          <SidebarContent
            collapsed={false}
            onToggle={undefined}
            userGroups={userGroups}
            groups={groups}
            givenName={givenName}
            familyName={familyName}
            signOut={signOut}
            onNavigate={onClose}
          />
        </div>
      </div>
    </>
  );
}